import React from "react"
import { format } from "date-fns"
import { toZonedTime } from "date-fns-tz"

import EquipmentChit from "./EquipmentChit"

const DAY_MS = 86400000

const plural = (n, one, many) => (n === 1 ? one : many)

/**
 * Booking days as text - "26-30 Oct", "28 Oct - 2 Nov", "26 Oct". Bookings end the day
 * after their last day (equipmentBooking.model.js).
 */
const days = (startDate, endDate) => {
    const first = toZonedTime(startDate)
    const last = toZonedTime(endDate - DAY_MS)

    if (startDate >= endDate - DAY_MS) return format(first, "d MMM")

    return first.getMonth() === last.getMonth()
        ? `${format(first, "d")}-${format(last, "d MMM")}`
        : `${format(first, "d MMM")} - ${format(last, "d MMM")}`
}

/**
 * What happened to the machine bookings of the equipment requests whose dates changed (the
 * `equipment` part of a schedule refit - api planItemSchedule.shift.js). Shown in a confirm
 * once it's done, so whoever changed the dates knows what to rebook.
 */
const EquipmentRefitNotice = ({ equipment }) => {
    const unitById = new Map((equipment.units || []).map((unit) => [unit._id, unit]))
    const typeById = new Map((equipment.types || []).map((type) => [type._id, type]))

    const { unbooked = [], notExtended = [], stayed = [], released = [] } = equipment

    const list = (items, note) => (
        <div style={{ width: "100%", marginBottom: 14 }}>
            {items.map((item, i) => (
                <div
                    key={`${item.equipment}-${i}`}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}
                >
                    <EquipmentChit
                        item={unitById.get(item.equipment) || { title: "Equipment" }}
                        typeById={typeById}
                        width={30}
                    />
                    <div className="dna-smaller-text" style={{ textAlign: "right", marginLeft: 12 }}>
                        {note(item)}
                    </div>
                </div>
            ))}
        </div>
    )

    const counts = [
        equipment.moved && `${equipment.moved} moved`,
        equipment.trimmed && `${equipment.trimmed} shortened`,
        equipment.extended && `${equipment.extended} extended`,
    ].filter(Boolean)

    const hasLists = unbooked.length || notExtended.length || stayed.length || released.length

    return (
        <div style={{ width: "100%", maxHeight: "50vh", overflowY: "auto", textAlign: "left" }}>
            {unbooked.length > 0 && (
                <>
                    <p>
                        <b>
                            {unbooked.length} {plural(unbooked.length, "machine", "machines")} couldn't move with the
                            new dates
                        </b>
                    </p>
                    <p>
                        {plural(unbooked.length, "It is", "They are")} already booked elsewhere on those days, so{" "}
                        {plural(unbooked.length, "its booking", "their bookings")} on this project{" "}
                        {plural(unbooked.length, "was", "were")} released. Please book{" "}
                        {plural(unbooked.length, "a replacement", "replacements")}:
                    </p>
                    {list(unbooked, (item) => `Needed ${days(item.startDate, item.endDate)}`)}
                </>
            )}

            {notExtended.length > 0 && (
                <>
                    <p>
                        <b>
                            {notExtended.length} {plural(notExtended.length, "booking", "bookings")} couldn't be made
                            longer
                        </b>
                    </p>
                    <p>
                        The request needs {plural(notExtended.length, "this machine", "these machines")} for more days
                        now, but {plural(notExtended.length, "it is", "they are")} booked elsewhere then.{" "}
                        {plural(notExtended.length, "The booking", "The bookings")} stayed as{" "}
                        {plural(notExtended.length, "it was", "they were")}, so the request is only partly covered:
                    </p>
                    {list(
                        notExtended,
                        (item) =>
                            `Booked ${days(item.startDate, item.endDate)}, needed ${days(
                                item.neededStart,
                                item.neededEnd,
                            )}`,
                    )}
                </>
            )}

            {stayed.length > 0 && (
                <>
                    <p>
                        <b>
                            {stayed.length} {plural(stayed.length, "machine is", "machines are")} already on site
                        </b>
                    </p>
                    <p>
                        {plural(stayed.length, "Its booking has", "Their bookings have")} started, so{" "}
                        {plural(stayed.length, "it", "they")} kept {plural(stayed.length, "its", "their")} start date.
                        Only the end followed the new dates:
                    </p>
                    {list(stayed, (item) => `Now ${days(item.startDate, item.endDate)}`)}
                </>
            )}

            {released.length > 0 && (
                <>
                    <p>
                        <b>
                            {released.length} {plural(released.length, "booking is", "bookings are")} no longer needed
                        </b>
                    </p>
                    <p>
                        The request no longer covers {plural(released.length, "its", "their")} days, so{" "}
                        {plural(released.length, "it was", "they were")} released:
                    </p>
                    {list(released, (item) => `Was ${days(item.startDate, item.endDate)}`)}
                </>
            )}

            {counts.length > 0 && (
                <p>
                    {hasLists ? (
                        `Other machine bookings: ${counts.join(", ")}.`
                    ) : (
                        <>
                            <b>Machine bookings updated to the new dates</b>
                            <br />
                            {counts.join(", ")}.
                        </>
                    )}
                </p>
            )}
        </div>
    )
}

export default EquipmentRefitNotice
